/**
 * The Indicator values panel's pure half — §D3 manual submissions, client-side.
 *
 * D.2 and E.1 are `source: 'manual'`: nothing computes them, somebody reports them. The
 * panel's job is to make that report a file the server already knows how to check, so
 * everything here exists to produce exactly one artefact — the CSV the validator reads
 * (`apps.indicators.submissions`) — and nothing here touches React, the DOM or the
 * network.
 *
 * **The header is the template's, character for character.** `unit_id, unit_name, country,
 * value, period, note` is `apps.indicators.serializers.TEMPLATE_HEADER`, which is both what
 * `/indicators/templates` hands a reporter and what `submissions.validate` looks its
 * columns up by. A grid that typed the column `name` instead would produce a file the
 * server reads as having no unit names at all — informational, so it would pass, and the
 * reviewer would see eighty blank names and no error saying why.
 *
 * **A blank cell stays blank.** An empty `value` is a legitimate answer — "this unit did
 * not report" — and the submission counts it (`diff.blank`) rather than failing on it. So
 * `null`, `undefined` and `''` all become an empty field, never `0` and never `"null"`.
 */
import type { GridState } from '../grid';
import { dirtyCount } from '../grid';

/**
 * The levels a submission may name — `apps.indicators.submissions.LEVELS`, verbatim and in
 * its order. Three admin levels, §D3's two overlay hierarchies, and the unit-less
 * programme pseudo-level, which answers with a single row whose `unit_id` is empty.
 */
export const LEVELS = ['0', '1', '2', 'watershed', 'partnership', 'programme'] as const;

export type Level = (typeof LEVELS)[number];

/**
 * A level out of a string, or `null` when it names none.
 *
 * The select's `value` is a `string` as far as the DOM is concerned, and casting it to
 * `Level` would put a typo — or a stale permalink, or a hand-edited option — into state as
 * a level the server has never heard of, which surfaces as a 422 three requests later.
 * Narrowing here makes the refusal local and the cast unnecessary.
 */
export function parseLevel(value: string): Level | null {
  return (LEVELS as readonly string[]).includes(value) ? (value as Level) : null;
}

/** The programme's reporting window (`submissions.MIN_PERIOD`/`MAX_PERIOD`). */
export const MIN_PERIOD = 2020;
export const MAX_PERIOD = 2035;

/** Every year the period select offers, oldest first. */
export const PERIODS: number[] = Array.from(
  { length: MAX_PERIOD - MIN_PERIOD + 1 },
  (_, i) => MIN_PERIOD + i,
);

/**
 * The year the panel opens on: this one, clamped into the window rather than dropped out
 * of it — a clock past 2035 must still land the select on a real option.
 */
export const defaultPeriod = (now: Date = new Date()): number =>
  Math.min(MAX_PERIOD, Math.max(MIN_PERIOD, now.getFullYear()));

/**
 * The grid row id of the single unit-less row a `programme` submission has.
 *
 * `/current` answers that row with `id: ''`, and an empty string is a poor row id: it is
 * what `initGrid` would key the row by, what a test id would end in, and what a `find` for
 * a missing row would match. The placeholder is local to the panel — `toCsv` writes the
 * empty `unit_id` back, because that is what the server reads.
 */
export const PROGRAMME_ROW_ID = 'programme';

/** A unit's grid row id — its own, or the programme placeholder for the unit-less row. */
export const rowIdOf = (unit: { id: string }): string =>
  unit.id === '' ? PROGRAMME_ROW_ID : unit.id;

/** The one extension a submission may arrive as — `submissions.validate` reads a CSV. */
export const CSV_EXTENSION = '.csv';

export const isCsvFile = (name: string): boolean =>
  name.toLowerCase().endsWith(CSV_EXTENSION);

/** `apps.indicators.serializers.TEMPLATE_HEADER`. */
export const CSV_HEADER = ['unit_id', 'unit_name', 'country', 'value', 'period', 'note'] as const;

/** One row as the panel holds it, before it becomes a line of CSV. */
export interface ValueRow {
  unit_id: string;
  /** The unit's name, written out under the header's `unit_name`. */
  name: string;
  country: string | null;
  value: number | string | null;
  note: string;
}

/**
 * One RFC 4180 field.
 *
 * Quoted only when it has to be — a comma, a quote, a CR or an LF — because a file whose
 * every field is quoted is harder for a reporter to read back in a text editor, which is
 * exactly what they do when a check fails. Inside quotes, `"` doubles.
 */
export function csvField(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';
  const text = String(value);
  if (text === '') return '';
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/**
 * The submission, as the bytes that go up.
 *
 * CRLF line endings and a trailing one: RFC 4180 says CRLF, Python's `csv` reader accepts
 * either, and Excel on Windows — which is where most of these files will be opened again —
 * only reliably round-trips the former.
 */
export function toCsv(rows: readonly ValueRow[], period: number): string {
  const lines = [CSV_HEADER.join(',')];
  for (const row of rows) {
    lines.push(
      [
        csvField(row.unit_id),
        csvField(row.name),
        csvField(row.country),
        csvField(row.value),
        csvField(period),
        csvField(row.note),
      ].join(','),
    );
  }
  return `${lines.join('\r\n')}\r\n`;
}

/** How many rows the submit would change — the panel's dirty counter. */
export const dirtyRows = (state: GridState): number => dirtyCount(state);

/**
 * The filename the submission travels under — `d2-2026-level0.csv`.
 *
 * It is what the reviewer sees in the queue before they open anything, so it names the
 * three things that decide what the file is about. Sanitised to the same alphabet the
 * server's own `safe_filename_part` keeps: an indicator id is server-supplied, and a
 * filename is not the place to discover that one of them carries a slash.
 */
export function submissionFilename(indicatorId: string, period: number, level: Level): string {
  const safe = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return `${safe(indicatorId) || 'indicator'}-${period}-level${safe(level) || '0'}.csv`;
}

/**
 * The `POST /uploads/{id}/metadata` body for a submission.
 *
 * Exactly the four keys `apps.datasets.views_uploads`' allowlist takes for this kind, plus
 * the `title` every upload row shows in the queue. `country` is upper-cased here rather
 * than trusted from a select, and `'ALL'` — the portal's own word for "no filter" — is
 * simply not sent, because the server validates it as an ISO3 code.
 */
export function submissionMetadata(opts: {
  indicatorId: string;
  indicatorName: string;
  period: number;
  level: Level;
  country?: string | null;
}): { indicator_id: string; period: number; level: Level; title: string; country?: string } {
  const country = (opts.country ?? '').trim().toUpperCase();
  return {
    indicator_id: opts.indicatorId,
    period: opts.period,
    level: opts.level,
    title: `${opts.indicatorName} ${opts.period}`,
    ...(country && country !== 'ALL' ? { country } : {}),
  };
}
